import {TitleBox} from "../general/TitleBox";
import {useAuth} from "../auth/AuthHook";
import {Empty, Table} from "antd";
import {LockIcon, SlackIcon} from "../components/Icons";
import React, {ReactPropTypes, useCallback, useEffect, useState} from "react";
import {useSupportCase} from "../support/CaseHook";
import {ActionButton} from "../components/ActionButton";
import {withBase} from "../utils/basePath";
import {useTranslation} from "react-i18next";
import {Tooltip} from "flowbite-react";
import '../filter/CaseFilter.css'

interface InstalledSlackConnection {
    bot_id: string
    enterprise_id?: string
    enterprise_name?: string
    team_id: string
    team_name: string
    bot_user_id: string
    bot_token_expires_at: number
    is_enterprise_install: boolean
}

interface SlackConfig {
    slack_enabled: boolean
    redirect_uri?: string
    socket_mode_enabled?: boolean
    client_id?: string
}

export function SlackSettings() {
    const [config, setConfig] = useState<SlackConfig | null>(null);
    const {isLoggedIn, refreshToken, isAdmin} = useAuth()
    const {fetchData} = useSupportCase()
    const [installations, setInstallations] = useState<InstalledSlackConnection[]>([]);
    const {t} = useTranslation();

    useEffect(() => {
        const fetchInstallations = async () => {
            if (!isLoggedIn()) {
                return;
            }
            try {
                const resConfig = await fetchData("integrations/slack/config", "GET");
                const fetchedConfig = await resConfig.json() as SlackConfig;
                setConfig(fetchedConfig);
                if (!fetchedConfig.slack_enabled) {
                    return;
                }
                const res = await fetchData("integrations/slack/installations", "GET");
                const installations: InstalledSlackConnection[] = await res.json();
                setInstallations(installations);
            } catch (e) {
                // Handled already
            }
        }
        fetchInstallations();
    }, [isLoggedIn, fetchData]);

    const handleInstallSlack = useCallback(async () => {
        await refreshToken();
        // Redirect to Slack installation URL
        window.location.replace(withBase("integrations/slack/install"));
    }, [refreshToken]);

    const handleDeleteInstallation = useCallback(async (record: InstalledSlackConnection) => {
        try {
            await fetchData(`integrations/slack/installations/${record.bot_id}`, "DELETE");
            setInstallations(prev => prev.filter(i => i.bot_id !== record.bot_id));
        } catch (e) {
            // Handled already
        }
    }, [fetchData]);

    const isPossibleToInstall = (config?.slack_enabled ?? false) && isAdmin;

    return (
        <div className="w-full lg:px-16 xl:px-32 bg-transparent">
            <TitleBox lineHidden={true}>
                <h1 className="text-2xl font-bold py-8">Slack Integration Settings</h1>
            </TitleBox>
            {
                !isLoggedIn() &&
                <Empty description={
                    <span className="text-gray-300 text-base">
                            You need to log in to see slack integrations.
                        </span>
                } image={<LockIcon/>}/>
            }
            {isLoggedIn() &&
                <div className={"mb-6 flex flex-col"}>
                    <Table<InstalledSlackConnection>
                        dataSource={installations}
                        columns={[
                            {
                                title: 'Team Name',
                                dataIndex: 'team_name',
                                key: 'team_name',
                            },
                            {
                                title: 'Enterprise Name',
                                dataIndex: 'enterprise_name',
                                key: 'enterprise_name',
                            },
                            {
                                title: 'Bot User ID',
                                dataIndex: 'bot_user_id',
                                key: 'bot_user_id',
                            },
                            {
                                title: 'Token Expiry',
                                dataIndex: 'bot_token_expires_at',
                                key: 'bot_token_expires_at',
                                render: (timestamp: number) => {
                                    const date = new Date(timestamp * 1000);
                                    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
                                }
                            },
                            {
                                title: 'Actions',
                                key: 'actions',
                                render: (_: unknown, record: InstalledSlackConnection) => (
                                    <Tooltip content={isAdmin ? t("Delete this Slack integration.") : t("Not an administrator.")}>
                                        <ActionButton
                                            className="text-left flex-row justify-start w-fit"
                                            onClick={() => handleDeleteInstallation(record)}
                                            disabled={!isAdmin}
                                        >
                                            {t("Delete")}
                                        </ActionButton>
                                    </Tooltip>
                                )
                            }
                        ]}
                        rowKey="bot_id"
                        pagination={false}
                        prefixCls={"arten-search"}
                        rowClassName={"case-filter-table-row"}
                        className={"case-filter-table"}
                        rootClassName={"case-filter-table-root"}
                        bordered={false}
                        sortDirections={["ascend", "descend"]}
                        showSorterTooltip={true}
                        components={
                            {
                                header: {
                                    wrapper: (props: ReactPropTypes) => (
                                        <thead {...props} className={"case-filter-table-header"}/>
                                    ),
                                },

                            }
                        }

                    />
                    <Tooltip
                        content={
                            config == null ? t("Loading...") :
                                config?.slack_enabled == false ? t("Slack integration is disabled.") :
                                    !isAdmin ? t("Not an administrator.") :
                                        t("Click to add Slack integration.")
                        }
                    >
                        <ActionButton className="text-left flex-row justify-start w-fit space-x-2 mt-4"
                                      onClick={handleInstallSlack}
                                      disabled={!isPossibleToInstall}


                        >
                            <SlackIcon/>
                            <span>
                            {t("Add to Slack")}
                        </span>
                        </ActionButton>
                    </Tooltip>
                </div>
            }

        </div>

    )
}